import React, { useState, useEffect } from 'react';
import { toast } from "react-toastify";
// import { useTranslation } from 'react-i18next';
import Modal from 'apollo-react/components/Modal';
import { Form, Field, FieldArray, withFormik } from 'formik';
import Typography from 'apollo-react/components/Typography';
import { FormikRadioGroup, FormikSelect, FormikSwitch, FormikTextField } from '../../../common/FormikComponent';
import axiosInstance from "../../../../axiosInstance";
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import MenuItem from 'apollo-react/components/MenuItem';

import validate from './builder_stages_validation';


const initialValues = {
    name: '',
    description: '',
    form_id: '',
    stage_type_id: 1,
    notification_type_id: '',
    target_category_id: '',
    approver_role_id: '',
    sla_enabled: false,
    sla_duration: '',
    action: 'form',
    nodeId: '',
    subflow_id: ''
};

const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
};

const BuilderStagesModal = ({ nodes, nodeStageId, content, onEvent, onConfirm, onDiscard, selectedCategory, usedSubmissionFormIds = [], setFieldTouched, setFieldError, values, setFieldValue }) => {
    const [showModal, setShowModal] = useState(false);
    // const [t] = useTranslation();
    const [slaEnabled, setSlaEnabled] = useState(false);
    const [loading, setLoading] = useState(true); 
    const [formsSelection, setFormsSelection] = useState([]);
    const [notificationTypeSelection, setNotificationTypeSelection] = useState([]);
    const [categoriesSelection, setCategoriesSelection] = useState([]);
    const [rolesSelection, setRolesSelection] = useState([]);
    const [stageTypesSelection, setStageTypesSelection] = useState([]);
    const [slaLabel, setSlaLabel] = useState("SLA of resubmitting after send back");

    useEffect(() => {
        setLoading(true);
        // If update, populate the form with existing content
        if (content) {
            // console.log("Populating form with content:", content);
            Object.keys(content).forEach((key) => {
                if (initialValues.hasOwnProperty(key)) {
                    setFieldValue(key, content[key]);
                }
            });

            // Synchronize the slaEnabled state with the sla_enabled value
            if (content.sla_enabled !== undefined) {
                setSlaEnabled(content.sla_enabled);
            }

            if (nodeStageId) {
                setFieldValue('nodeId', nodeStageId);
            }
        }
        setLoading(false);
    }, [content, setFieldValue]);

    useEffect(() => {
        // Find the selected stage type object
        const selectedStageType = stageTypesSelection.find(
            (stageType) => stageType.id === values.stage_type_id
        );
        if (
            selectedStageType?.type === "Submission" &&
            selectedCategory &&
            !values.target_category_id // Only set if not already set
        ) {
            setFieldValue('target_category_id', selectedCategory);
        }
    }, [selectedCategory, values.stage_type_id, stageTypesSelection, setFieldValue, values.target_category_id]);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const [formsResponse, notificationResponse, categoriesResponse, rolesResponse, StageTypesResponse] = await Promise.all([
                    axiosInstance.get("/api/forms/private"),
                    axiosInstance.get("/api/thresholds/notification-types"),
                    axiosInstance.get("/api/categories/public"),
                    axiosInstance.get("/api/roles/reviews/assigned"),
                    axiosInstance.get("/api/stage_types")
                ]);

                setFormsSelection(formsResponse.data);
                setCategoriesSelection(categoriesResponse.data);
                setNotificationTypeSelection(notificationResponse.data);
                setRolesSelection(rolesResponse.data);
                setStageTypesSelection(StageTypesResponse.data);
            
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, []);

    useEffect(() => {
        if (onEvent) {
            updateSlaLabel(1);
            
            setShowModal(true);
        }
    }, [nodes, onEvent]);

    // Check if it is the first submission in flow (To set "SLA_Enabled" OR "SLA of resubmitting after send back")
    const updateSlaLabel = (current_stage_type_id) => {

        // If workflow already has a submission node
        const getSubmissionNodesExists = nodes.some(
            (node) => node.category === "Submission"
        );

        if (getSubmissionNodesExists) {
            setSlaLabel("SLA Enabled");
        }
    };

    const handleCloseModal = () => {
        // TODO: If node is not from update then remove the node
        onDiscard(nodeStageId);

        setShowModal(false);
        onEvent({ open: false, sentData: null });
    };

    const slaAvail = (event, setFieldValue) => {
        const { checked } = event.target;
        setSlaEnabled(checked);
        setFieldValue('sla_enabled', checked);
    };

    const actionTypeChange = (event, setFieldValue) => {
        const { value } = event.target;
        setFieldValue('stage_type_id', parseInt(value, 10));
        setFieldValue('approver_role_id', ''); // Reset the "Review" selection

        // Find the selected stage type object
        const selectedStageType = stageTypesSelection.find(
            (stageType) => stageType.id === parseInt(value, 10)
        );

        if (selectedStageType?.type === "Submission" && selectedCategory) {
            setFieldValue('target_category_id', selectedCategory);
        } else if (selectedStageType?.type !== "Submission") {
            setFieldValue('target_category_id', '');
        }

        setFieldValue('form_id', '');

        updateSlaLabel(value);
    };

    const handleSave = async (validate, values) => {
        const errors = await validate(values);

        if (Object.keys(errors).length === 0) {
            toast.success("Stage added successfully");
            onConfirm(nodeStageId, values);
            setShowModal(false);
        } else {
            Object.keys(errors).forEach((field) => {
            setFieldTouched(field, true, false);
            setFieldError(field, errors[field]);
            });
        }
    };

    return (
        <>
            <Modal open={showModal} variant="default" title={"Add Stage"} id="stage_creation"
                // disableBackdropClick
                onClose={() => handleCloseModal()}
                buttonProps={[
                    { label: 'Go Back', onClick: handleCloseModal },
                    { label: 'Save', onClick: () => handleSave(validate, values, setFieldTouched, setFieldError) }
                ]}
            >
                {loading ? (
                    <Typography variant="body2" style={{ margin: '8px 0' }}>
                       {'Loading...'}
                    </Typography>
                ) : (
                    <Form noValidate>
                        <FormikTextField fullWidth name="name" label="Name" key="name" required autoFocus />

                        <FormikTextField fullWidth name="description" label="Description" key="description" multiline sizeAdjustable />

                        <RadioGroup
                            label="Action type"
                            aria-label="label"
                            name="stage_type_id"
                            key={values.stage_type_id}
                            value={values.stage_type_id}
                            onChange={(e) => actionTypeChange(e, setFieldValue)}
                            >
                            {stageTypesSelection?.map((stageType) => (
                                <Radio 
                                key={stageType.id}
                                value={stageType.id}
                                label={stageType.type} />
                            ))}
                        </RadioGroup>

                        {/* Category or Role based on selection */}
                        {stageTypesSelection?.map((stageType) => (
                            stageType.type === "Submission" && values.stage_type_id === stageType.id ? (
                                <FormikSelect
                                    name="target_category_id"
                                    label="End-User Category"
                                    key="target_category_id"
                                    fullWidth
                                    required
                                    onChange={(e) => { setFieldValue('target_category_id', e.target.value); }}
                                    disabled={!!selectedCategory} // Disable if selectedCategory is set
                                >
                                    <MenuItem value="">{'Select Category'}</MenuItem>
                                    {categoriesSelection?.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                                    ))}
                                </FormikSelect>
                            ) : stageType.type === "Review" && values.stage_type_id === stageType.id ? (
                                <FormikSelect
                                    name="approver_role_id"
                                    label="Role"
                                    key="approver_role_id"
                                    fullWidth
                                    required
                                    onChange={(e) => { setFieldValue('approver_role_id', e.target.value); }}
                                >
                                    <MenuItem value="">{'Select Role'}</MenuItem>
                                    {rolesSelection?.map((role) => (
                                        <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                                    ))}
                                </FormikSelect>
                            ) : null
                        ))}

                        <FormikSelect 
                            name="form_id" 
                            key={`form_id_${values.stage_type_id}_${nodeStageId}`} 
                            label="Form" 
                            fullWidth 
                            required
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        overflow: 'auto'
                                    }
                                }
                            }}
                        >
                            <MenuItem value="">{'Select a form'}</MenuItem>
                            {formsSelection?.map((form) => (
                                <MenuItem
                                key={form.id}
                                value={form.id}
                                disabled={
                                    stageTypesSelection.find(st => st.id === values.stage_type_id)?.type === "Submission" &&
                                    usedSubmissionFormIds.includes(form.id)
                                }
                                >
                                {form.name}
                                </MenuItem>
                            ))}
                        </FormikSelect>

                        <Field type="hidden" name="action" key="action" value="form" />

                        <Field type="hidden" name="nodeId" key="nodeId" />

                        <Field type="hidden" name="subflow_id" value="" key="subflow_id" />

                        <FormikSelect name="notification_type_id" label="Notification Type" key="notification_type_id" fullWidth>
                            <MenuItem value="">{'Select Notification Type'}</MenuItem>
                            {notificationTypeSelection?.map((notification) => (
                                <MenuItem key={notification.id} value={notification.id}>{notification.type}</MenuItem>
                            ))}
                        </FormikSelect>

                        <FormikSwitch
                        name="sla_enabled"
                        checked={values.sla_enabled}
                        onChange={(e) => slaAvail(e, setFieldValue)}
                        // label change based on the first submission
                        label={slaLabel}
                        key="sla_enabled"
                         />

                        {slaEnabled && (
                            <FormikTextField type="number" fullWidth name="sla_duration" label="SLA Duration" />
                        )}
                    </Form>
                )}
            </Modal>
        </>
    );
};

// export default BuilderStagesModal;
export default withFormik({
  validate,
  mapPropsToValues: () => initialValues,
  handleSubmit,
})(BuilderStagesModal);
